import { useContextSelector } from 'use-context-selector';
import { mapValues, merge, pick } from 'lodash';
import React, { useCallback } from 'react';
import { REQUIRED_PROFILE_FIELDS } from 'app/stores/user';
import { OnboardingDataType, OnboardingContext } from './OnboardingContext';

export interface OnboardingFieldProps {
  onChange: (data: {
    values: Record<string, any>;
    validate: () => { valid: boolean; errorMessage?: string } | Promise<{ valid: boolean; errorMessage?: string }>;
  }) => void;
  values: Record<string, any>;
  error?: string;
}

const UPDATABLE_PROFILE_FIELDS = ['addressLine2', 'currentState', ...REQUIRED_PROFILE_FIELDS];

export const OnboardingField: React.FC<{
  children: (props: {
    values: Partial<OnboardingDataType>;
    error: any;
    onChange: (...any: any[]) => void;
  }) => JSX.Element;
  fields: string[];
  errorFieldName: string;
}> = ({ children, fields, errorFieldName }) => {
  const values = useContextSelector(OnboardingContext, (v) => pick(v.cache, fields));
  const error = useContextSelector(OnboardingContext, (v) => v.errors[errorFieldName]);
  const setState = useContextSelector(OnboardingContext, (v) => v.setState);

  const handleChange = useCallback(
    async ({
      values: v,
      validate,
    }: {
      values: Record<string, any>;
      validate: () => { valid: boolean; errorMessage?: string } | Promise<{ valid: boolean; errorMessage?: string }>;
    }) => {
      if (!setState) {
        return;
      }
      const { valid, errorMessage = '' } = await Promise.resolve(validate());

      const permittedValues = pick(v, UPDATABLE_PROFILE_FIELDS);

      if (!valid) {
        setState((draft) => {
          draft.cache = merge(draft.cache, permittedValues);
          draft.errors[errorFieldName] = errorMessage;
          draft.profile = merge(
            draft.profile,
            mapValues(permittedValues, () => null)
          );
        });
        return;
      }

      setState((draft) => {
        draft.cache = merge(draft.cache, permittedValues);
        draft.profile = merge(draft.profile, permittedValues);
        draft.errors[errorFieldName] = null;
      });
    },
    [setState, errorFieldName]
  );

  return (
    <>
      {typeof children === 'function'
        ? children({
            values,
            error,
            onChange: handleChange,
          })
        : null}
    </>
  );
};
